// Copy every surface shares. The words are the glossary's (spec/01-glossary.md): it is a
// `station`, never a terminal, machine or seat; a `session`, a `pass`, a `lease`, a `venue`, a
// `zone`, a `member`, a `player`.
import problems from './problems.js';
export const common = {
    // What a refusal says, by the catalogue's code (spec/08 §2.6); see `problems.ts`.
    problems,
    product: {
        name: 'Clavian',
        tagline: 'The keys to your venue.',
    },
    action: {
        save: 'Save',
        saving: 'Saving',
        saved: 'Saved',
        retry: 'Try again',
        signIn: 'Sign in',
        signOut: 'Sign out',
        lockRoom: 'Lock the room',
        unlockStation: 'Unlock the station',
        endSession: 'End the session',
        extend: 'Add time',
    },
    entity: {
        station: 'Station',
        station_other: 'Stations',
        venue: 'Venue',
        zone: 'Zone',
        session: 'Session',
        pass: 'Pass',
        lease: 'Lease',
        member: 'Member',
        player: 'Player',
    },
    stationState: {
        available: 'Available',
        inSession: 'In session',
        locked: 'Locked',
        offline: 'Offline',
        maintenance: 'Maintenance',
    },
    status: {
        online: 'Online',
        offline: 'Offline',
        paid: 'Paid',
        overdue: 'Overdue',
        endingSoon: 'Ending soon',
    },
    session: {
        timeLeft: 'Time left',
        timeLeftOn: 'Time left on {{station}}',
        endedOn: "Time's up on {{station}}.",
        startedAt: 'Started {{time}}',
    },
    count: {
        stationsFree_one: '{{count}} station free',
        stationsFree_other: '{{count}} stations free',
    },
    room: {
        locked: 'Room locked. {{count}} station.',
        locked_other: 'Room locked. {{count}} stations.',
    },
    payment: {
        takenTotal: 'Paid. {{total}}',
        takenSplit: 'Paid. {{total}} · {{methods}}',
    },
    error: {
        offline: 'No connection to the venue. Showing the last known state.',
        generic: 'That did not go through.',
    },
    // The one-time code emails (issue #407; spec/07 §2.1 `emailOTP`, spec/16 §5). The server renders
    // them — `apps/server/src/modules/notifications/email-templates.ts` — as a subject, a plain-text
    // part and an HTML part. `{{product}}` is the brand's product name (spec/03 §12). The code is
    // never in the subject: a subject is what a locked phone shows on its lock screen.
    emailCode: {
        subject: {
            signIn: 'Your {{product}} sign-in code',
            emailVerification: 'Confirm your email address for {{product}}',
            forgetPassword: 'Your {{product}} password reset code',
            changeEmail: 'Confirm your new email address for {{product}}',
        },
        intro: {
            signIn: 'Enter this code to sign in to {{product}}.',
            emailVerification: 'Enter this code to confirm your email address.',
            forgetPassword: 'Enter this code to choose a new {{product}} password.',
            changeEmail: 'Enter this code to confirm your new email address.',
        },
        expires_one: 'It expires in {{count}} minute and works once.',
        expires_other: 'It expires in {{count}} minutes and works once.',
        notYou: 'If you did not ask for this code, ignore this email. Nothing changes without it.',
        neverShare: 'Never share it with anyone, including {{product}} staff.',
    },
    // The one-time code as one SMS (N-01, F-KIO-04; spec/16 §1–2). The server renders it —
    // `apps/server/src/modules/notifications/email-templates.ts` — into a single line. `{{product}}`
    // is the brand's product name (spec/03 §12) and `{{code}}` the code, which lives only in the body.
    // Kept short: an SMS is one segment, and Indian gateways bill and (with DLT) template per segment.
    smsCode: {
        signIn: '{{code}} is your {{product}} sign-in code.',
        passwordReset: '{{code}} is your {{product}} password reset code.',
        phoneChange: '{{code}} is your {{product}} verification code.',
        expires_one: 'It expires in {{count}} minute and works once. Never share it.',
        expires_other: 'It expires in {{count}} minutes and works once. Never share it.',
    },
    // N-25, "invited to an organisation" (spec/16 §4.3, F-IAM-08), as an email.
    staffInvitation: {
        subject: 'You are invited to join {{organization}} on {{product}}',
        intro: '{{organization}} has invited you to join its staff as {{role}}.',
        action: 'Accept the invitation',
        howTo: 'Open the link and sign in with this email address. If you do not have a {{product}} account yet, create one with this address first.',
        expires_one: 'The link works once and expires in {{count}} day.',
        expires_other: 'The link works once and expires in {{count}} days.',
        notYou: 'If you were not expecting this, ignore this email. Nothing changes unless you accept.',
    },
    // N-25 for a café's first owner, invited by HQ (ADR-0046, spec/16 §4.3): a welcome to the Platform — who invited
    // them to run which café, what happens next, the button, the expiry and the fallback link. A staff invitation keeps
    // `staffInvitation` above. Customer copy: no spec sections, feature ids or internal names.
    ownerWelcome: {
        subject: 'Welcome to {{product}}: set up {{organization}}',
        heading: "Welcome to {{product}}. Let's get {{organization}} ready.",
        intro: 'The {{product}} team has invited you to run {{organization}} on {{product}}, as its owner.',
        nextTitle: 'What happens next',
        step1: 'Open the link and create your {{product}} account with this email address, or sign in if you already have one.',
        step2: 'Accept the invitation. You become the owner of {{organization}}.',
        step3: 'A short guided setup takes you through your business details, your first venue and going live.',
        closing: 'It only takes a few minutes, and we are here if you need us.',
        action: 'Set up {{organization}}',
        expires_one: 'The link works once and expires in {{count}} day.',
        expires_other: 'The link works once and expires in {{count}} days.',
        notYou: 'If you were not expecting this, ignore this email. Nothing is set up unless you accept.',
    },
    // N-25 as one SMS — the phone fallback when an invitation is sent to a number (spec/16 §4.3,
    // F-IAM-08). It carries the accept link, which is the invitation: `{{url}}` is that link, built
    // from CONSOLE_URL (`access/tokens.ts`), and works only for someone signed in with this number.
    smsInvitation: {
        body: 'You are invited to join {{organization}} on {{product}} as {{role}}. Accept: {{url}}',
        // A café's first owner, invited by HQ (ADR-0046).
        owner: 'Welcome to {{product}}. You are invited to run {{organization}} as its owner. Start here: {{url}}',
    },
    // N-14, "new sign-in on a new device / password or two-factor changed", and the account-lock
    // notice (spec/16 §4.3, spec/07 §2.2, §3.3; F-IAM-12), by email and by SMS. The server renders them
    // — `apps/server/src/modules/notifications/email-templates.ts`. Nothing secret is ever in one: no
    // code, no password, no token, and a new address or number only masked. `{{time}}` is UTC.
    securityNotice: {
        subject: {
            newDevice: 'New sign-in to your {{product}} account',
            passwordChanged: 'Your {{product}} password was changed',
            twoFactorChanged: 'Two-factor authentication changed on your {{product}} account',
            phoneChanged: 'The phone number on your {{product}} account was changed',
            emailChanged: 'The email address on your {{product}} account was changed',
            accountLocked: 'Signing in to your {{product}} account is paused',
            emailAdded: 'Finish setting up your {{product}} account',
            stationSignIn: 'You signed in at a PC with your {{product}} account',
            accountLinked: 'A sign-in was linked to your {{product}} account',
        },
        body: {
            newDevice: 'Your account was signed in to on {{device}}, a device we had not seen before, at {{time}}, using {{method}}.',
            newDeviceUnknown: 'Your account was signed in to on a device we had not seen before, at {{time}}, using {{method}}.',
            passwordChanged: 'The password on your account was changed at {{time}}.',
            twoFactorEnabled: 'Two-factor authentication was turned on for your account at {{time}}.',
            twoFactorDisabled: 'Two-factor authentication was turned off for your account at {{time}}.',
            twoFactorBackupCodes: 'New backup codes were made for your account at {{time}}. The old ones no longer work.',
            phoneChanged: 'The phone number on your account was changed to {{value}} at {{time}}.',
            emailChanged: 'The email address on your account was changed to {{value}} at {{time}}.',
            accountLocked: 'After five wrong sign-in attempts, signing in to your account is paused until {{until}}.',
            emailAdded: 'This email address was added to your account at a PC at {{time}}, and you can now sign in with it.',
            stationSignIn: 'You signed in at {{station}}, {{venue}}, at {{time}}.',
            accountLinked: '{{provider}} was linked to your account at {{station}}, {{venue}}, at {{time}}. You can now sign in with it.',
            emailAddedPassword: 'To add a password, choose “Forgot password” when you sign in with this address in the {{product}} app or on the web, and follow the code we send.',
        },
        method: {
            phoneOtp: 'a code sent to your phone',
            email: 'your email address and password',
            username: 'your gamer tag and password',
            emailOtp: 'a code sent to your email',
            password: 'your password and your authenticator',
            passkey: 'a passkey',
            google: 'Google',
            discord: 'Discord',
            steam: 'Steam',
            unknown: 'a sign-in we could not name',
        },
        ifYou: 'If this was you, there is nothing to do.',
        ifNotYou: 'If it was not you, sign in, end the sessions you do not recognise under Account, then Security, and change your password.',
        ifNotYouStation: 'Not you? End that session now, then change your password.',
        ifNotYouLinked: 'If it was not you, sign in, unlink it under Account, then Connected accounts, and change your password.',
        endSession: 'End that session',
        unlinkAndSecure: 'Unlink it and secure your account',
        ifNotYouLocked: 'If it was not you, someone may be trying to guess your password. Change it once you can sign in again.',
        neverAsk: '{{product}} never asks for your password or a code by email, message or phone.',
    },
    // The footer of every email the platform sends: where to get help. `{{email}}` is the brand
    // configuration's support address (spec/03 §12 Identity), never a hard-coded one.
    emailFooter: {
        support: 'Questions? Write to {{email}}.',
    },
    // The shared email layout (`apps/server/src/modules/notifications/email-layout.ts`): the inbox
    // preview line (preheader) of each email, and its footer — who sent it and why it arrived. A
    // preheader shows on a locked phone, so it never carries a code.
    emailLayout: {
        preheader: {
            code: 'Your one-time {{product}} code is inside. Never share it.',
            invitation: '{{organization}} has invited you to join its staff on {{product}}.',
            ownerWelcome: 'You are invited to run {{organization}} on {{product}}.',
        },
        sentBy: 'Sent by {{product}}.',
        sentByVenue: 'Sent by {{venue}} with {{product}}.',
        reason: {
            code: 'You received this email because this address was entered to sign in to, or confirm an address on, {{product}}.',
            invitation: 'You received this email because {{organization}} invited this address to join its staff on {{product}}.',
            ownerWelcome: 'You received this email because the {{product}} team invited this address to set up {{organization}} on {{product}}.',
            securityNotice: 'You received this email because it is about the security of your {{product}} account. Security notices cannot be turned off.',
            notification: 'You received this email because of your notification preferences on {{product}}.',
            receipt: 'You received this email because a receipt for a purchase at {{venue}} was sent to this address.',
            dailySummary: 'You received this email because you turned on the daily summary in your notification settings on {{product}}. Only owners receive it. To stop it, turn it off there.',
            dailySummaryRequested: 'You received this email because you asked {{product}} to send you this summary.',
            moved: 'You received this email because you had an account at {{venue}} in its previous system, and {{venue}} has moved to {{product}}. It tells you what was moved and what you can do about it.',
        },
        linkFallback: 'If the button does not work, copy this link into your browser:',
    },
    // The "account moved" email and its two reminders (F-IAM-13, ADR-0048 §13), under the café's name. It is also the
    // notice of what was moved for the player: what it is, why it is kept, how long, and how to have it deleted. Rendered
    // by `apps/server/src/modules/notifications/moved-email.ts`. `{{venue}}` is the venue's name, `{{platform}}` the old
    // system's.
    movedEmail: {
        subject: {
            moved: 'Your {{venue}} account has moved',
            after_switch: 'Your balance at {{venue}} is waiting for you',
            before_end: 'Last month to claim your balance at {{venue}}',
        },
        preheader: {
            moved: '{{venue}} has moved to {{product}}. Your balance came with you.',
            after_switch: 'Claim your balance at {{venue}} before {{date}}.',
            before_end: 'Claim your balance at {{venue}} by {{date}}, or it will be closed.',
        },
        heading: {
            moved: '{{venue}} has moved to {{product}}',
            after_switch: 'Your balance is still waiting',
            before_end: 'One month left to claim your balance',
        },
        greeting: 'Hi {{name}},',
        intro: {
            moved: '{{venue}} no longer uses {{platform}}. Your account there, @{{username}}, has moved to {{product}}, with what you had paid for.',
            after_switch: 'Your account @{{username}} from {{platform}} has moved to {{product}}, and what you had paid for is still waiting for you.',
            before_end: 'What you had paid for at {{venue}} under @{{username}} is still waiting. Claim it by {{date}}.',
        },
        waiting: 'Waiting for you',
        field: {
            money: 'Money',
            moneyTaxPaid: 'Of it, GST already paid',
            time: 'Play time',
            timeUntil: 'Play time valid until',
            claimBy: 'Claim by',
        },
        claim: 'Claim my balance',
        howTitle: 'How to claim it',
        how: {
            link: 'Use the button above and sign in, or create a free account, with this email address.',
            pc: 'Or at any PC at {{venue}}: choose "Moving from our old system?" and enter your old username or this email.',
            counter: 'Or at the front desk: show photo ID and staff will move it to your account.',
        },
        counterOnly: 'Please claim it at the front desk of {{venue}}. Bring photo ID, and if you are under 18, a parent or guardian.',
        noticeTitle: 'What was moved, and why',
        notice: 'From {{platform}}, {{venue}} moved your username, name, email, phone number, date of birth, the date you joined and your last visit, any staff note, and your balances. They are kept only so that you can claim what is yours.',
        keep: 'If you do not claim it by {{date}}, your details will be deleted and the balance will be closed.',
        delete: 'To have your details deleted sooner, or to ask what is held, ask the staff at {{venue}} or write to {{support}}.',
        deleteNoSupport: 'To have your details deleted sooner, or to ask what is held, ask the staff at {{venue}}.',
    },
    // A sale's document by email (F-POS-13, spec/06 §10.2): a bill, a bill of supply or a tax invoice,
    // titled by its kind — a bill is never called an invoice. Rendered by
    // `apps/server/src/modules/notifications/receipt-email.ts`. `{{venue}}` is the venue's name.
    receiptEmail: {
        subject: {
            bill: 'Your bill from {{venue}}',
            bill_of_supply: 'Your bill of supply from {{venue}}',
            tax_invoice: 'Your tax invoice from {{venue}}',
        },
        title: {
            bill: 'Bill',
            bill_of_supply: 'Bill of supply',
            tax_invoice: 'Tax invoice',
        },
        heading: 'Thanks for visiting {{venue}}',
        intro: 'Here is your {{document}} for your purchase. Keep it for your records.',
        preheader: '{{document}} {{number}}, total {{total}}.',
        field: {
            number: 'Number',
            date: 'Date',
            gstin: 'GSTIN',
            venue: 'Venue',
            buyer: 'Billed to',
            buyerGstin: 'Buyer GSTIN',
            placeOfSupply: 'Supply',
        },
        placeOfSupply: {
            intra_state: 'Intra-state (CGST + SGST)',
            inter_state: 'Inter-state (IGST)',
        },
        column: {
            item: 'Item',
            quantity: 'Qty',
            amount: 'Amount',
            hsnSac: 'HSN/SAC',
            rate: 'Rate',
            unitPrice: 'Price',
            taxable: 'Taxable',
            tax: 'GST',
        },
        hsnSac: 'HSN/SAC {{code}}',
        each: '{{price}} each',
        subtotal: 'Subtotal',
        promotion: 'Offer: {{name}}',
        discountCode: 'Code {{code}}',
        discountManual: 'Discount',
        taxComponent: '{{component}} {{rate}}',
        taxSummary: 'GST in these prices',
        taxableValue: 'Taxable value',
        total: 'Total',
        paidWith: 'Paid by {{tender}}',
        cashReceived: 'Cash received',
        change: 'Change',
        changeToWallet: 'Change added to {{player}}’s wallet',
        changeToWalletUnnamed: 'Change added to the player’s wallet',
        refunded: 'Refunded',
        tender: {
            cash: 'cash',
            counter_card: 'card',
            counter_upi: 'UPI',
            wallet: 'wallet',
            on_account: 'account',
            online: 'online (UPI/card)',
        },
        compositionDeclaration: 'Composition taxable person, not eligible to collect tax on supplies.',
        // A guest pass's ticket on the printed receipt.
        guestTicket: {
            title: 'Guest ticket',
            hint: 'Type this code on a PC under “I have a ticket”.',
            until: 'Works until {{time}}',
            claim: 'Save your time and earn points: create an account at {{venue}} and claim this code.',
        },
        view: 'View your {{document}}',
        totalTax: 'Total GST',
        taxPaidAtTopUp: 'Of this GST, {{amount}} was already paid when this money was topped up in the café’s previous system. It is not charged again.',
        amountInWords: 'Amount charged',
        creditNotes: 'Credit notes',
        creditNote: 'Credit note {{number}} against {{invoice}}: {{total}} (GST {{tax}})',
        printedAt: 'Printed {{when}}',
        computerGenerated: 'This is a computer-generated document.',
    },
    // The owners' end-of-day summary (N-27): the email, and its line in the inbox. Rendered by
    // `apps/server/src/modules/notifications/daily-summary-email.ts`. `{{name}}` is the organisation's or the venue's
    // name, `{{date}}` the business date ("Monday 6 October"), `{{weekday}}` its weekday. Plain words: takings, play,
    // sessions, players — counts only, never a player's name.
    dailySummaryEmail: {
        subject: '{{name}} daily summary for {{date}}',
        preheader: 'Net takings {{amount}}. {{sessions}}. {{attention}}',
        heading: {
            organization: 'Your day across {{name}}',
            venue: 'Your day at {{name}}',
        },
        lead: {
            up: 'Net takings were {{amount}}, up {{percent}} on last {{weekday}}.',
            down: 'Net takings were {{amount}}, down {{percent}} on last {{weekday}}.',
            flat: 'Net takings were {{amount}}, the same as last {{weekday}}.',
            noBase: 'Net takings were {{amount}}. Last {{weekday}} took nothing to compare with.',
        },
        allClear: 'Nothing needs your attention.',
        attention_one: '{{count}} thing needs your attention.',
        attention_other: '{{count}} things need your attention.',
        sessions_one: '{{count}} session',
        sessions_other: '{{count}} sessions',
        vsLastWeek: '{{change}} vs last {{weekday}}',
        vsAverage: '{{change}} vs 7-day average',
        sameAsLastWeek: 'Same as last {{weekday}}',
        sameAsAverage: 'Same as the 7-day average',
        noComparison: 'Nothing to compare with',
        tile: {
            netTakings: 'Net takings',
            grossSales: 'Gross sales',
            sessions: 'Sessions',
            hoursPlayed: 'Hours played',
            utilisation: 'Occupancy',
            players: 'Players',
        },
        playersDetail_one: '{{count}} new',
        playersDetail_other: '{{count}} new',
        section: {
            highlights: 'Highlights',
            venues: 'Venues',
            takings: 'Takings',
            byTender: 'How it was paid',
            play: 'Play',
            food: 'Food, drink and retail',
            wallet: 'Wallets and credit',
            cash: 'Cash',
            players: 'Players',
        },
        flag: {
            day_not_closed: '{{venue}}: the day has not been closed yet. These figures may still change.',
            // The variance's sign picks the word; the amount is always shown without one ("₹700.00 short", never "off by -₹700.00").
            cash_variance_short: '{{venue}}: the cash count was {{amount}} short.',
            cash_variance_over: '{{venue}}: the cash count was {{amount}} over.',
            refunds_high: '{{venue}}: refunds of {{amount}} are well above usual.',
            shift_open_one: '{{venue}}: {{count}} shift is still open.',
            shift_open_other: '{{venue}}: {{count}} shifts are still open.',
            station_offline_one: '{{venue}}: {{count}} station was offline for 30 minutes or more during a session.',
            station_offline_other: '{{venue}}: {{count}} stations were offline for 30 minutes or more during a session.',
            stock_alerts_one: '{{venue}}: {{count}} stock item needs attention.',
            stock_alerts_other: '{{venue}}: {{count}} stock items need attention.',
        },
        best: 'Best venue: {{venue}}, with {{amount}} in net takings.',
        column: {
            venue: 'Venue',
            netTakings: 'Net takings',
            sessions: 'Sessions',
            inUse: 'Occupancy',
            vsLastWeek: 'vs last week',
        },
        notClosed: 'Not closed yet',
        bestBadge: 'Best',
        takings: {
            grossSales: 'Gross sales',
            discounts: 'Discounts',
            refunds_one: 'Refunds ({{count}})',
            refunds_other: 'Refunds ({{count}})',
            tax: 'GST collected',
            netTakings: 'Net takings',
            moneyIn: 'Money in, wallet top-ups included',
            onAccount: 'Sold on account, not paid yet',
        },
        tender: {
            cash: 'Cash',
            counter_card: 'Card',
            counter_upi: 'UPI at the counter',
            online: 'Online',
            wallet: 'From wallets',
        },
        play: {
            hoursPlayed: 'Hours played',
            averageSession: 'Average session',
            utilisation: 'Occupancy while open',
            revenuePerStation: 'Net takings per station',
            passSales: 'Play sold as passes',
            payAsYouGo: 'Play at the hourly rate',
        },
        food: {
            foodSales: 'Food and drink',
            retailSales: 'Retail',
            orders: 'Orders',
            topItems: 'Best sellers',
            item: '{{name}} × {{quantity}}',
            noItems: 'Nothing sold',
            stockAlerts: 'Stock items needing attention',
        },
        wallet: {
            topUps: 'Wallet top-ups',
            bonusIssued: 'Bonus credit given',
            spent: 'Spent from wallets',
            onAccountOwed: 'Owed on account across your venues',
        },
        cash: {
            expected: 'Expected in the drawers',
            counted: 'Counted',
            variance: 'Over or short',
            drawer: '{{counter}} drawer',
            drawerUnnamed: 'Drawer',
            drawerOpen: 'Still open',
            openShifts: 'Shifts still open',
            noDrawers: 'No drawer was counted.',
        },
        players: {
            unique: 'Players',
            new: 'New players',
        },
        duration: '{{hours}} h {{minutes}} min',
        action: 'Open the report',
        inbox: {
            title: 'Daily summary: {{name}}, {{date}}',
            body: 'Net takings {{amount}}. {{sessions}}. {{attention}}',
        },
    },
};
export default common;
//# sourceMappingURL=common.js.map